import { cx } from "@emotion/css";
import { format } from "date-fns";
import { CircleHelp } from "lucide-react";
import { useTranslation } from "react-i18next";
import SimpleProgressBar from "src/components/Default/LekUI/SimpleProgressBar";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import type { ChatbotLimits } from "src/components/GlobalChatbot/types";

interface ChatbotLimitBarProps {
	limits: ChatbotLimits;
}

const PERIOD_LABEL_FALLBACK: Record<ChatbotLimits["period"], string> = {
	daily: "pytań dziś",
	weekly: "pytań w tym tygodniu",
	monthly: "pytań w tym miesiącu",
	last30Days: "pytań w ostatnich 30 dniach",
};

const PERIOD_CADENCE_FALLBACK: Record<ChatbotLimits["period"], string> = {
	daily: "codziennie",
	weekly: "co tydzień",
	monthly: "co miesiąc",
	last30Days: "co 30 dni",
};

export const ChatbotLimitBar = ({ limits }: ChatbotLimitBarProps) => {
	const { t } = useTranslation();

	if (limits.max === null) {
		return null;
	}

	const limitReached = limits.left === 0;
	const value = Math.min(100, (limits.used / limits.max) * 100);

	const tooltipText = t(
		"globalChatbot.limits.tooltip",
		"Limit odnawia się {{cadence}} o {{time}}",
		{
			cadence: t(
				`globalChatbot.limits.cadence.${limits.period}`,
				PERIOD_CADENCE_FALLBACK[limits.period],
			),
			time: format(new Date(limits.resetsAt), "HH:mm"),
		},
	);

	return (
		<div className="chatbot-limit-bar-row">
			<SimpleProgressBar
				className={cx("chatbot-limit-bar", {
					"chatbot-limit-bar--exhausted": limitReached,
				})}
				color="primary"
				variant="determinate"
				value={value}
			/>
			<span
				data-test-id="global-chatbot-limits"
				className={cx("size-10 text-grey chatbot-limit-text", {
					"text-error font-bold": limitReached,
				})}
			>
				{limits.used}/{limits.max}{" "}
				{t(
					`globalChatbot.limits.period.${limits.period}`,
					PERIOD_LABEL_FALLBACK[limits.period],
				)}
			</span>
			<Tooltip title={tooltipText}>
				<CircleHelp
					data-test-id="global-chatbot-limits-info"
					className="chatbot-limit-info"
				/>
			</Tooltip>
		</div>
	);
};
